import {FC, useState} from "react";
import {CreditCard, Mail, CheckCircle, X} from "lucide-react";
import axios from "api/axios";
import useMaybeNotSetupUser from "contexts/MaybeNotSetupUser/useMaybeNotSetupUser";

const AccountSettings: FC<{email: string; showBilling?: boolean}> = ({email, showBilling = true}) => {
  const {user} = useMaybeNotSetupUser();
  const pending = (user as {pendingEmailChange?: {email: string; taskId: string | null} | null} | null)
    ?.pendingEmailChange;
  // If current email matches the pending request, the change was already fulfilled
  const pendingEmail = pending && pending.email !== email ? pending.email : undefined;

  const [billingSent, setBillingSent] = useState(false);
  const [billingSending, setBillingSending] = useState(false);

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(!!pendingEmail);
  const [emailError, setEmailError] = useState<string | null>(null);

  const sendBillingLink = async () => {
    if (billingSending) return;
    setBillingSending(true);
    try {
      await axios.post("/user/accountant/billing-portal", {email});
      setBillingSent(true);
    } catch {
      // silently fail
    } finally {
      setBillingSending(false);
    }
  };

  const submitEmailChange = async () => {
    if (emailSubmitting || !newEmail.trim()) return;
    setEmailSubmitting(true);
    setEmailError(null);
    try {
      await axios.post("/user/request-email-change", {newEmail: newEmail.trim()});
      setEmailSubmitted(true);
      setEmailModalOpen(false);
    } catch (err: unknown) {
      const msg = (err as {response?: {data?: {error?: string}}})?.response?.data?.error;
      setEmailError(
        msg === "A request is already pending" ? "A request is already pending." : msg || "Something went wrong.",
      );
    } finally {
      setEmailSubmitting(false);
    }
  };

  return (
    <>
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Manage Billing */}
          {showBilling && (
            <div className="rounded-xl border border-brand-border bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-brand-cream flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-4 h-4 text-brand-navy" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-brand-navy">Manage Billing</h3>
                  <p className="text-xs text-brand-muted mt-0.5 mb-2">
                    Update credit card, view subscriptions &amp; payment history.
                  </p>
                  {billingSent ? (
                    <div className="flex items-center gap-1.5 text-xs text-green-600">
                      <CheckCircle className="w-3.5 h-3.5" />
                      <span>
                        Login link sent to <strong>{email}</strong>
                      </span>
                      <button
                        onClick={() => setBillingSent(false)}
                        className="ml-1 underline opacity-70 hover:opacity-100"
                      >
                        Resend
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={sendBillingLink}
                      disabled={billingSending}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                      style={{backgroundColor: "#F57A47"}}
                    >
                      {billingSending ? "Sending..." : "Send Login Link"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Change Email */}
          <div className="rounded-xl border border-brand-border bg-white p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-brand-cream flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-brand-navy" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-brand-navy">Change Email</h3>
                <p className="text-xs text-brand-muted mt-0.5 mb-2">
                  Current: <strong>{email}</strong>
                </p>
                {emailSubmitted || pendingEmail ? (
                  <div className="flex items-center gap-1.5 text-xs text-green-600">
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span>Request submitted{pendingEmail ? ` (${pendingEmail})` : ""}</span>
                  </div>
                ) : (
                  <button
                    onClick={() => setEmailModalOpen(true)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-opacity hover:opacity-90 border border-brand-border text-brand-navy"
                  >
                    Request Email Change
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email change modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl border border-brand-border shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-brand-navy">Request Email Change</h3>
              <button
                onClick={() => setEmailModalOpen(false)}
                className="text-brand-muted hover:text-brand-navy transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-brand-muted mb-3">
              Current email: <strong>{email}</strong>
            </p>
            <label className="block text-xs font-medium text-brand-navy mb-1">New email address</label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="new@example.com"
              className="w-full px-3 py-2 rounded-lg border border-brand-border text-sm text-brand-navy focus:outline-none focus:border-brand-amber"
              onKeyDown={(e) => e.key === "Enter" && submitEmailChange()}
            />
            {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setEmailModalOpen(false)}
                className="text-xs font-medium px-3 py-1.5 rounded-lg text-brand-muted hover:text-brand-navy transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitEmailChange}
                disabled={emailSubmitting || !newEmail.trim()}
                className="text-xs font-semibold px-4 py-1.5 rounded-lg text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{backgroundColor: "#F57A47"}}
              >
                {emailSubmitting ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountSettings;
